import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  createSerialColumn,
  DrillDownCount,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
  toHeaderHintMeta,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { formatDisplayDateTime } from '@/utils/date';
import { formatNumber, pluralize } from '@/utils/number';
import { DCR_TYPE_COLUMNS } from '../constants/dashboard.constants';
import type {
  DcrDashboardRow,
  DcrDashboardSearch,
  DcrWorkSummarySearch,
} from '../types/dashboard.types';

function renderWorkCount(
  member: DcrDashboardRow,
  count: number,
  entryKind: string,
  summarySearch: DcrWorkSummarySearch,
) {
  const hint = `View ${formatNumber(count)} ${entryKind} ${pluralize(count, 'entry', 'entries')}`;
  return (
    <DrillDownCount
      count={count}
      hint={hint}
      ariaLabel={`${hint} for ${member.name}`}
      render={
        <Link
          to="/dashboards/dcr/$userId"
          params={{ userId: member.userId }}
          search={summarySearch}
        />
      }
    />
  );
}

export function getDcrDashboardColumns(
  dashboardSearch: DcrDashboardSearch,
  searchText: string | undefined,
): ColumnDef<DcrDashboardRow, unknown>[] {
  return [
    createSerialColumn<DcrDashboardRow>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => <MemberNameCell member={row.original} searchText={searchText} />,
    },
    {
      id: 'department',
      header: 'Department / Designation',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.departmentName ?? <EmptyValue />}
          secondary={row.original.designationName}
        />
      ),
    },
    getAreaColumn<DcrDashboardRow>(),
    {
      accessorKey: 'totalDcr',
      header: 'Total DCR',
      cell: ({ row }) =>
        renderWorkCount(row.original, row.original.totalDcr, 'DCR', dashboardSearch),
    },
    ...DCR_TYPE_COLUMNS.map((column) => ({
      accessorKey: column.key,
      header: column.label,
      meta: toHeaderHintMeta(column.title),
      cell: ({ row }: { row: { original: DcrDashboardRow } }) =>
        renderWorkCount(row.original, row.original[column.key], column.title, {
          ...dashboardSearch,
          dcrTypeId: column.id,
        }),
    })),
    {
      accessorKey: 'lastUpdate',
      header: 'Last Upload',
      cell: ({ row }) =>
        row.original.lastUpdate ? (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatDisplayDateTime(row.original.lastUpdate)}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
  ];
}
